import React, { useCallback, useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ModalInstruments } from 'components/ModalInstruments';
import { ModalFileUpload } from 'components/ModalFileUpload';
import ReactTooltip from 'react-tooltip';

import { interestApi } from 'helpers/api/interest';
import { Client } from 'store/client/actions';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';

import { clientSelector, errorSelector, clientInterestSelector } from 'store/client/selectors';

import { balanceStockSelector } from 'store/transferAct/selectors';

import { defaultToast, errorToast, successToast } from 'configuration/notifications';
import { downloadFileFromBase64, getUnixTimestamp } from 'helpers/utils';

import { Header } from 'components/Header';
import { Icon } from 'components/Icon';
import { HiddenTextArea } from 'components/HiddenTextArea';
import { Loader } from 'components/Loader';
import { ButtonIcon } from 'components/Button';

import { getDocumentText } from './helpers';
import { ClientBlock } from './components/ClientBlock';
import { ClientRequisites } from './components/ClientRequisites';
import { ButtonLabel } from 'components/Button/ButtonLabel';

import './style.scss';
import { transferApi } from 'helpers/api/transfer';
import { clientApi } from 'helpers/api/client';
import { BaseService } from 'services';
import { toast } from 'react-toastify';
import cn from 'classnames';
import { infoToast } from 'configuration/notifications';

const contextMenuID = 'ClientPageContextMenuID';

const RENT_MENU = 1;
const INSTRUMENT_MENU = 2;
const INTEREST_MENU = 3;
const COMMENTARY_MENU = 4;
const AGREEMENT_MENU = 5;
const DOCUMENT_MENU = 6;

const ClientPage = () => {
  const { clientId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const uploadInputRef = useRef();

  const client = useSelector(clientSelector);
  const clientInterest = useSelector(clientInterestSelector);
  const error = useSelector(errorSelector);
  const stockID = useSelector(balanceStockSelector);

  const [loadRent, setLoadRent] = useState('');
  const [loadFeedback, setLoadFeedback] = useState(false);

  const [modalInstruments, setModalInstruments] = useState(false);
  const [modalFileUpload, setModalFileUpload] = useState(false);
  const [documentType, setDocumentType] = useState();

  const [openRent, setOpenRent] = useState([]);
  const [rentSubEntities, setRentSubEntities] = useState({});
  const [openFeedback, setOpenFeedback] = useState([]);

  const [agreements, setAgreements] = useState([]);
  const [rentMenu, setRentMenu] = useState(RENT_MENU);
  const [checkRentState, setCheckRentState] = useState(false);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    if (clientId) {
      dispatch(Client.getClient(clientId));
      return;
    }
    errorToast('Неверный Client ID');
    history.push('/');
  }, [clientId, dispatch, history]);

  useEffect(() => {
    if (client && !checkRentState) {
      const result = !!(client?.acts?.length && client?.instruments?.length);
      setRentMenu(result ? RENT_MENU : INTEREST_MENU);
      setCheckRentState(true);
    }
  }, [client, checkRentState]);

  useEffect(() => {
    if (client?.agreements) {
      setAgreements(client.agreements.reverse());
    }
  }, [client?.agreements]);

  const handleUpdate = (newData) => {
    dispatch(Client.updateClient(clientId, newData));
  };

  const handleBack = () => {
    const redirect = location?.state?.redirectFromReserve || location?.state?.redirectFromRent;
    if (!redirect) {
      return;
    }
    history.push(redirect, { fromClient: true, selected: null });
  };

  const handleSelect = () => {
    const redirect = location?.state?.fromReserve || location?.state?.fromRent;
    if (!redirect) {
      return;
    }

    history.push(redirect, { fromClient: true, selected: client });
  };

  const handleRentState = (value) => {
    return () => {
      setRentMenu(value);
    };
  };

  const handleNewRentWithCurrentClient = () => {
    history.push('/rent', { fromClient: true, selected: client });
  };

  const handleNewReservationWithCurrentClient = () => {
    history.push('/reservation', { fromClient: true, selected: client });
  };

  const handleSendClientTo1C = async () => {
    if(client.isTemporary){
      infoToast('Заполните полностью клиента для отправки в 1С');
      return;
    }
    if (client.isReadyFor1C || client.isCreatedFrom1C) {
      return;
    }
    dispatch(Client.sendClientTo1C(clientId));
  };

  // const show1CButton = useCallback(() => !client.isReadyFor1C && !client.isCreatedFrom1C, [client]);
  const show1CButton = useCallback(() => false, [client]);

  const handleChangeComment = (value) => {
    if (value.trim() !== client.comment) {
      handleUpdate({ comment: value.trim() });
    }
  };

  const handleClose = () => {
    if (!location.state || Object.keys(location.state).length === 0) {
      history.push('/');
      return;
    }

    const redirect = location.state.fromReserve || location.state.fromRent;
    if (!redirect) {
      history.goBack();
      return;
    }

    history.push(redirect, { fromClient: true, selected: null });
  };

  const handleOpenRent = async (id) => {
    const newOpenRents = openRent.slice();
    const newRentSubEntities = rentSubEntities;
    const index = newOpenRents.indexOf(id);

    if (index === -1) {
      newOpenRents.push(id);
      setOpenRent(newOpenRents);
      setLoadRent(id);
      const {
        data: { documents },
      } = await transferApi.getTransferActSubEntities(id);
      newRentSubEntities[id] = documents;
      setRentSubEntities(newRentSubEntities);
      setLoadRent('');
      return;
    }
    newOpenRents.splice(index, 1);
    delete newRentSubEntities[id];

    setRentSubEntities(newRentSubEntities);
    setOpenRent(newOpenRents);
  };

  const handleOpenFeedback = (id) => {
    const newOpenFeedbacks = openFeedback.slice();
    const index = newOpenFeedbacks.indexOf(id);

    if (index === -1) {
      newOpenFeedbacks.push(id);
    } else {
      newOpenFeedbacks.splice(index, 1);
    }
    setOpenFeedback(newOpenFeedbacks);
  };

  if (error) {
    errorToast('Во время загрузки произошла ошибка!');
    history.goBack();
  }

  if (!client) {
    return <Loader />;
  }

  const handleContextMenu = async (_, { actID1C }) => {
    const { data } = await transferApi.sendViberMessage(actID1C);
    if (data.ok) {
      successToast('Сообщение отправлено в вайбер!');
      return;
    }
    errorToast('Сообщение не отправлено в вайбер!');
  };

  const handleDocumentClick = (id) => {
    return async () => {
      const toastId = defaultToast('Происходит скачивание документа...', { autoClose: false });
      const { data } = await clientApi.getDocument(id);
      downloadFileFromBase64(data.name, data.file, data.format);
      toast.update(toastId, {
        render: 'Файл успешно скачан!',
        type: toast.TYPE.SUCCESS,
        className: 'success-toast',
        autoClose: 4000,
      });
    };
  };

  const handleItemAdd = async ({ itemID }) => {
    const clientID = client.id;
    try {
      const { data } = await interestApi.createInterest({ clientID, itemID, stockID });
      dispatch(Client.addCreatedClientInterestToList(data));
    } catch (error) {}
    setModalInstruments(false);
  };

  const handleClickType = (data) => {
    handleModalFileUpload();
    setDocumentType(data);
    uploadInputRef.current.click();
  };

  const handleUploadFileTelegram = () => {
    window.open(`${process.env.REACT_APP_DOC_BOT_URL}${clientId}`, '_blank');
  };

  const handleClickDisabledButton = () => {
    errorToast('Клиент помечен на удаление! Оформление невозможно!');
  };

  const handleModalInstruments = () => {
    setModalInstruments(!modalInstruments);
  };

  const handleModalFileUpload = () => {
    setModalFileUpload(!modalFileUpload);
  };

  const hasErrorsInFiles = (files) => {
    const extentionRegex = /(?:\.([^.]+))?$/;
    const validExtentions = ['pdf', 'jpg', 'png'];
    if (!files.length) {
      errorToast('Ошибка: Файлы не выбраны!');
      return true;
    }

    let error = false;

    Array.from(files).forEach((file) => {
      const fileExtention = extentionRegex.exec(file.name.toLowerCase())[1];
      if (!validExtentions.includes(fileExtention)) {
        error = true;
      }
    });

    if (error) {
      errorToast('Ошибка: загружен не валидный тип файла. Доступные типы: pdf, jpg, png.');
    }
    return error;
  };

  const handleOnFileUpload = async ({ target: { files } }) => {
    if (hasErrorsInFiles(files)) {
      return;
    }
    const data = await BaseService.getBase64FromFiles(files);
    const toastId = defaultToast('Происходит выгрузка файлов...', { autoClose: false });
    try {
      await clientApi.createDocument({
        files: data,
        clientID: clientId,
        doc_type: documentType,
      });
      toast.update(toastId, {
        render: 'Файлы успешно выгружены!',
        type: toast.TYPE.SUCCESS,
        className: 'success-toast',
        autoClose: 4000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Файлы не выгружены!',
        type: toast.TYPE.ERROR,
        className: 'error-toast',
        autoClose: 4000,
      });
    }
    uploadInputRef.current.value = '';

    dispatch(Client.getClient(clientId));
  };

  const getTimeRange = (endDate) => {
    const now = getUnixTimestamp();
    const seconds = Math.abs(endDate - now);

    const days = Math.floor(seconds / (3600 * 24));
    if (days === 0) {
      return '';
    }
    if (days > 365) {
      return 'прошло больше года';
    }

    const preDateStr = now > endDate ? 'прошло' : 'истекает через';
    const postDateStr = days % 10 === 1 ? 'сутки' : 'суток';
    return `${preDateStr} ${days} ${postDateStr}`;
  };

  const selectableClient = location.state && (location.state.fromReserve || location.state.fromRent);
  const backClient = location.state && (location.state.redirectFromReserve || location.state.redirectFromRent);
  const labelList = [
    { status: 'Ожидает обработки', classColor: 'column-button--gr' },
    { status: 'Ожидает выдачи инструмента', classColor: 'column-button--bl' },
    { status: 'В аренде', classColor: 'column-button--ye' },
    { status: 'Истекает срок', classColor: 'column-button--or' },
    { status: 'Завершено', classColor: 'column-button--gr' },
  ];

  return (
    <>
      <Header
        title={
          <>
            {client.FIO}
            {client.isCreatedFromDiia ? (
              <div style={{ marginBottom: '10px', marginLeft: '5px' }}>
                <Icon name="created-from-diia" />
              </div>
            ) : (
              ''
            )}
          </>
        }
        onClose={handleClose}
      ></Header>

      <main className="client-main">
        <div className="client-column">
          <ClientBlock title="Реквизиты">
            <ClientRequisites client={client} onChange={handleUpdate} historyParams={location.state} />
          </ClientBlock>
          <div style={{ display: 'flex' }}>
            <ClientBlock width="250px" minSize="60px" invisible={true} title="Комментарий">
              <HiddenTextArea
                prefix={!!client.comment ? '' : 'Добавить коментарий'}
                value={client.comment}
                onChange={(value) => handleChangeComment(value)}
              />
            </ClientBlock>
            <ClientBlock width="250px" invisible={true} title="Бонусный счёт">
              <div className="client-block-body">
                Остатки на счёту: <b>{client.bonusAccount} грн.</b>
              </div>
            </ClientBlock>
          </div>
        </div>

        <div className="client-column">
          <ClientBlock
            width="700px"
            minSize="350px"
            title=""
            scroll={rentMenu !== INTEREST_MENU}
            headerButtons={
              <>
                <ButtonLabel onClick={handleRentState(RENT_MENU)} active={rentMenu === RENT_MENU} text="Аренды" />
                <ButtonLabel
                  onClick={handleRentState(INSTRUMENT_MENU)}
                  active={rentMenu === INSTRUMENT_MENU}
                  text="Оборудование"
                />
                <ButtonLabel
                  onClick={handleRentState(INTEREST_MENU)}
                  active={rentMenu === INTEREST_MENU}
                  text="Интересы"
                />
                <ButtonLabel
                  onClick={handleRentState(COMMENTARY_MENU)}
                  active={rentMenu === COMMENTARY_MENU}
                  text="Отзывы"
                />
                <ButtonLabel
                  onClick={handleRentState(AGREEMENT_MENU)}
                  active={rentMenu === AGREEMENT_MENU}
                  text={
                    <div className="agreement-list-button">
                      Договоры с ФОП{' '}
                      {!!client.agreements.filter((agr) => agr.term >= getUnixTimestamp()).length && (
                        <div className="count">
                          {client.agreements.filter((agr) => agr.term >= getUnixTimestamp()).length}
                        </div>
                      )}
                    </div>
                  }
                />
                <ButtonLabel
                  onClick={handleRentState(DOCUMENT_MENU)}
                  active={rentMenu === DOCUMENT_MENU}
                  text={
                    <div className="document-list-button">
                      Документы {!!client?.documents?.length && <div className="count">{client.documents.length}</div>}
                    </div>
                  }
                />
              </>
            }
          >
            {rentMenu === RENT_MENU &&
              client?.acts &&
              client.acts.map((act) => {
                const isOpen = openRent.includes(act.actID1C);
                const oneOf = labelList.find((label) => act.status === label.status);
                const labelColor = oneOf?.classColor || 'column-button--gr';
                return (
                  <ContextMenuTrigger key={act.actID1C} id={contextMenuID} collect={() => ({ ...act })}>
                    <div
                      className={`client-item ${isOpen ? 'is-active' : ''}`}
                      onClick={() => handleOpenRent(act.actID1C)}
                    >
                      <div className="client-item-prop">
                        <Icon
                          name={isOpen ? 'minus' : 'plus'}
                          style={{ display: 'inline-block', marginRight: '10px' }}
                        />
                        <div>
                          Аренда с <b>{moment(act.startDateTime * 1000).format('hh:mm DD.MM')}</b> по{' '}
                          <b>{moment(act.endDateTime * 1000).format('hh:mm DD.MM')}</b>
                        </div>
                        <ButtonLabel className={labelColor} text={act.status} />
                      </div>
                    </div>
                    {loadRent && loadRent === act.actID1C && <span className="client-item-prop">Загрузка...</span>}
                    {isOpen &&
                      !loadRent &&
                      rentSubEntities[act.actID1C].map((doc, j) => (
                        <div className="client-item" key={j}>
                          <div className="client-item-prop" style={{ marginLeft: '5px' }}>
                            <Icon name="document" style={{ display: 'inline-block', marginRight: '10px' }} />

                            <div>{getDocumentText(doc, act)}</div>
                          </div>
                        </div>
                      ))}
                  </ContextMenuTrigger>
                );
              })}

            {rentMenu === INSTRUMENT_MENU && client.instruments && (
              <>
                {client.instruments.map((instrument) => {
                  return (
                    <React.Fragment key={instrument.id}>
                      <div
                        data-tip={`${instrument.is_rent ? 'Аренда' : 'Резерв'} ${getTimeRange(instrument.end_date)}`}
                        data-for={'client-instrument-rent-tooltip'}
                        className={cn('client-instrument', !instrument.is_rent && 'highlighted')}
                      >
                        <div className="client-instrument-prop">
                          {instrument.item_name}{' '}
                          <b>
                            {' '}
                            ({moment(instrument.start_date * 1000).format('hh:mm DD.MM.YY')} -{' '}
                            {moment(instrument.end_date * 1000).format('hh:mm DD.MM.YY')})
                          </b>{' '}
                          ({instrument.stock_name})
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
            {rentMenu === INTEREST_MENU && (
              <div className="interest">
                <div className="interest-content scrollable">
                  {Boolean(clientInterest.length) &&
                    clientInterest.map((interest) => {
                      return (
                        <React.Fragment key={interest.id}>
                          <div className="client-instrument">
                            <div className="client-instrument-prop">
                              {interest.item_name} <b>({moment(interest.dateTime).format('hh:mm DD.MM.YYYY')})</b> (
                              {interest.stock_name})
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  {!Boolean(clientInterest.length) && <span className="no-interest">У клиента еще нет интересов</span>}
                </div>
                <div className="interest-footer">
                  <ButtonIcon
                    className="btn-main-interest btn-save-interest"
                    icon="interest-select"
                    onClick={handleModalInstruments}
                    text="Добавить"
                  />
                </div>
              </div>
            )}
            {rentMenu === COMMENTARY_MENU &&
              client?.act_feedbacks &&
              client.act_feedbacks.map((act) => {
                const isOpen = openFeedback.includes(act.actID1C);
                const oneOf = labelList.find((label) => act.status === label.status);
                const labelColor = oneOf?.classColor || 'column-button--gr';
                return (
                  <React.Fragment key={act.actID1C}>
                    <div
                      className={`client-item ${isOpen ? 'is-active' : ''}`}
                      onClick={() => handleOpenFeedback(act.actID1C)}
                    >
                      <div className="client-item-prop">
                        <Icon
                          name={isOpen ? 'minus' : 'plus'}
                          style={{ display: 'inline-block', marginRight: '10px' }}
                        />

                        <div>
                          Аренда с <b>{moment(act.startDateTime * 1000).format('hh:mm DD.MM')}</b> по{' '}
                          <b>{moment(act.endDateTime * 1000).format('hh:mm DD.MM')}</b>
                        </div>
                        <ButtonLabel className={labelColor} text={act.status} />
                      </div>
                    </div>
                    {isOpen &&
                      act.feedbacks.map((doc, j) => (
                        <div className="client-item" key={j}>
                          <div className="client-item-prop" style={{ marginLeft: '18px' }}>
                            <div>{doc}</div>
                          </div>
                        </div>
                      ))}
                  </React.Fragment>
                );
              })}

            {rentMenu === AGREEMENT_MENU &&
              agreements
                .filter((agr) => agr.term >= getUnixTimestamp())
                .map((agr, i) => (
                  <div key={agr.id} className="client-item">
                    <div className="client-item-prop">
                      <span className={i === 0 ? 'bold' : ''}>
                        Договор с {agr.organization.orgName} от {moment(agr.date * 1000).format('hh:mm DD.MM.YYYY')}
                      </span>
                    </div>
                  </div>
                ))}
            {rentMenu === DOCUMENT_MENU && (
              <div className="documents">
                <div className="documents-content scrollable">
                  {client?.documents &&
                    client.documents.map((doc) => (
                      <div
                        key={doc.id}
                        data-tip
                        data-for="client-document-upload-tooltip"
                        className="client-item client-documents"
                        onClick={handleDocumentClick(doc.id)}
                      >
                        <div className="client-item-prop-icon">
                          <Icon name={doc.isCreatedFromDIIA ? 'client-document-diia' : 'client-document'} />
                        </div>
                        <div className="client-item-prop">
                          <span className="bold">{doc.file_name}</span>
                        </div>
                      </div>
                    ))}
                </div>
                <div className="documents-footer">
                  <input
                    type="file"
                    className="upload-input"
                    ref={uploadInputRef}
                    multiple
                    onChange={handleOnFileUpload}
                  />

                  <ButtonIcon
                    className="btn-main-interest btn-save-interest"
                    icon="interest-select"
                    onClick={handleModalFileUpload}
                    text="Добавить"
                  />
                  <ButtonIcon
                    className="btn-main-interest btn-telegram"
                    icon="telegram-min"
                    onClick={handleUploadFileTelegram}
                    text="Добавить"
                  />
                </div>
              </div>
            )}
          </ClientBlock>
        </div>
      </main>

      <ReactTooltip place="bottom" id="client-document-upload-tooltip">
        Скачать документ
      </ReactTooltip>
      <ReactTooltip place="bottom" id="client-instrument-rent-tooltip" />

      <ReactTooltip place="bottom" id="client-instrument-reserve-tooltip">
        Резерв
      </ReactTooltip>
      {!selectableClient && !backClient && !client.isPendingDeletion && (
        <div className="buttons-block-grid">
          <div></div>
          <div className="buttons-block">
            <ButtonIcon
              className="btn-main btn-save"
              icon="client-select"
              onClick={handleNewRentWithCurrentClient}
              text="Оформить аренду"
            />

            <ButtonIcon
              className="btn-main btn-save"
              icon="client-select"
              onClick={handleNewReservationWithCurrentClient}
              text="Оформить резерв"
            />
          </div>
          {show1CButton() && (
            <div className="buttons-block">
              <ButtonIcon className="btn-main btn-1C" icon="1C" onClick={handleSendClientTo1C} text="Выгрузить в 1С" />
            </div>
          )}
        </div>
      )}
      <ContextMenu id={contextMenuID} className="modal-instrument-context-menu">
        <MenuItem onClick={handleContextMenu}>Отправить запрос на отзыв в Viber</MenuItem>
      </ContextMenu>

      {!selectableClient && !backClient && client.isPendingDeletion && (
        <div className="buttons-block" style={{ margin: '0' }}>
          <ButtonIcon
            className="btn-main btn-save btn-disable "
            icon="client-select"
            onClick={handleClickDisabledButton}
            text="Оформить аренду"
          />
          <ButtonIcon
            className="btn-main btn-save btn-disable"
            icon="client-select"
            onClick={handleClickDisabledButton}
            text="Оформить резерв"
          />
        </div>
      )}
      {selectableClient && (
        <div className="buttons-block" style={{ margin: '0' }}>
          <ButtonIcon className="btn-main btn-save" onClick={handleSelect} icon="client-select" text="Выбрать" />
        </div>
      )}

      {backClient && (
        <div className="buttons-block-grid">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ButtonIcon className="btn-main" onClick={handleBack} icon="arrow-left" text="Вернуться к оформлению" />
          </div>
        </div>
      )}

      {modalInstruments && (
        <ModalInstruments onAdd={handleItemAdd} disableSelectable={stockID === null} onClose={handleModalInstruments} />
      )}
      {modalFileUpload && <ModalFileUpload onClose={handleModalFileUpload} onClick={handleClickType} />}
    </>
  );
};

export { ClientPage };
